import { Injectable } from '@angular/core';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { formatDate } from '@angular/common';
import { HttpResponse } from '@angular/common/http';
import { Observable, map, catchError, of } from 'rxjs';
import { ApiRequestService } from '@app/_services/api-Services/api-request.service';
import { EmployeeDto } from '@app/shared/model/employee.model';
import { EmployeeStatusEnum } from '@app/shared/utilities/constants';
@Injectable({
  providedIn: 'root'
})
export class UtilityService {

  constructor(private apiRequestService: ApiRequestService) { }
  formatDate(date: any): string {
    if (!(date instanceof Date)) {
      date = new Date(date);
    }
  
    if (!isNaN(date.getTime())) {
      const year = date.getFullYear();
      const month = ('0' + (date.getMonth() + 1)).slice(-2); 
      const day = ('0' + date.getDate()).slice(-2); 
      return `${year}-${month}-${day}`;
    } else {
      console.error("Invalid date provided");
      return ''; 
    }
  }

  
  formatDateForLocale(date: any): string {
    if (!(date instanceof Date)) {
      date = new Date(date);
    }
    
    // Extract the parts needed for datetime-local input
    const year = date.getFullYear();
    const month = ('0' + (date.getMonth() + 1)).slice(-2); // Ensure 2 digits
    const day = ('0' + date.getDate()).slice(-2);          // Ensure 2 digits
    const hours = ('0' + date.getHours()).slice(-2);        // Ensure 2 digits
    const minutes = ('0' + date.getMinutes()).slice(-2);    // Ensure 2 digits
    
    // Return in 'YYYY-MM-DDTHH:MM' format
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  }
  formatDateForDatetimeLocal(date: Date): string {
    const pad = (n: number) => n < 10 ? '0' + n : n;
    const year = date.getFullYear();
    const month = pad(date.getMonth() + 1);  // getMonth() is zero-based
    const day = pad(date.getDate());
    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());
    
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  }
  
   markFormGroupTouched(formGroup: FormGroup) {
    Object.values(formGroup.controls).forEach(control => {
      if (control instanceof FormControl) {
        control.markAsTouched({ onlySelf: true });
      } else if (control instanceof FormGroup) {
        this.markFormGroupTouched(control); // Recursive check for nested FormGroups
      }
    });
  }
  markFormGroupTouchedProject(formGroup: FormGroup | FormArray) {
    Object.values(formGroup.controls).forEach(control => {
      if (control instanceof FormControl) {
        control.markAsTouched({ onlySelf: true });
      } else if (control instanceof FormGroup || control instanceof FormArray) {
        this.markFormGroupTouchedProject(control); // Recursive check for nested FormGroups or FormArrays
      }
    });
  }
  calculateOrdinal(date: Date): string {
    const day = date.getDate();
    const weekday = date.getDay(); // Sunday = 0, Monday = 1, ..., Saturday = 6

    // Get the first day of the current month
    const firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
    const firstWeekday = firstDay.getDay(); // Day of week for the first day of the month

    // Calculate which occurrence of the day it is
    let occurrence = Math.floor((day + firstWeekday - weekday) / 7) + 1;

    // Convert the occurrence to an ordinal string
    let ordinalSuffix;
    switch (occurrence) {
      case 1:
        ordinalSuffix = '1st';
        break;
      case 2:
        ordinalSuffix = '2nd';
        break;
      case 3:
        ordinalSuffix = '3rd';
        break;
      default:
        ordinalSuffix = `${occurrence}th`;
    }

    return ordinalSuffix;
  }
  formatTime(dateTime: string): string {
    const date = new Date(dateTime);
    let hours = date.getUTCHours();
    const minutes = date.getUTCMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    
    hours = hours % 12;
    hours = hours ? hours : 12; // the hour '0' should be '12'
    const minutesStr = minutes < 10 ? '0' + minutes : minutes;
  
    return `${hours}:${minutesStr} ${ampm}`;
  }
  
  getCombinedTime(startTime: string, endTime: string): string {
    const startFormatted = this.formatTime(startTime);
    const endFormatted = this.formatTime(endTime);
  
    return `${startFormatted} - ${endFormatted}`;
  }
  getCurrentDateTime(): string {
    const current = new Date();
  
    // Get the local time in the proper format for the datetime-local input (YYYY-MM-DDTHH:MM)
    const year = current.getFullYear();
    const month = ('0' + (current.getMonth() + 1)).slice(-2); // Months are zero-indexed
    const day = ('0' + current.getDate()).slice(-2);
    const hours = ('0' + current.getHours()).slice(-2);
    const minutes = ('0' + current.getMinutes()).slice(-2);
    
    // Return in the format "YYYY-MM-DDTHH:MM"
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  }

  formatDateStandard(date: any): any {
    return formatDate(date, 'dd-MM-yyyy', 'en-US');
  }

  // Employee-related utility methods
  getEmployees(): Observable<EmployeeDto[]> {
    const queryParams = {
      Status: EmployeeStatusEnum.All,
      Fields: '%22%22',
      SearchQuery: '%22%22',
      OrderBy: 'id asc',
      PageSize: 1000, // Get all employees for mapping
      Skip: 0,
    };
    
    return this.apiRequestService
      .getRequest<EmployeeDto[]>('Employee', queryParams, false)
      .pipe(
        map((response: HttpResponse<EmployeeDto[]>) => {
          return response.body || [];
        }),
        catchError(error => {
          console.error('Error loading employees:', error);
          return of([]);
        })
      );
  }

  mapEmployeeIdsToNames(employeeIds: (number | string)[], employees: EmployeeDto[]): { [key: string]: string } {
    const employeeMap: { [key: string]: string } = {};
    
    employeeIds.forEach(id => {
      const employee = employees.find(emp => emp.id === Number(id));
      if (employee) {
        employeeMap[id.toString()] = `${employee.firstName} ${employee.lastName}`;
      } else {
        employeeMap[id.toString()] = `Employee ${id}`;
      }
    });
    
    return employeeMap;
  }

  getEmployeeNameById(employeeId: number | string, employees: EmployeeDto[]): string {
    const employee = employees.find(emp => emp.id === Number(employeeId));
    return employee ? `${employee.firstName} ${employee.lastName}` : `Employee ${employeeId}`;
  }
  
}
